import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatEuro, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, AlertCircle } from "lucide-react";

const EMPTY_LINE = { designation: "", description: "", quantite: 1, unite: "u", prix_unitaire: 0, tva: 0 };
const newLine = () => ({ ...EMPTY_LINE, _uid: `l-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` });
const withUids = (lignes) => (lignes || []).map((l) => (l._uid ? l : { ...l, _uid: `l-${Math.random().toString(36).slice(2, 9)}` }));

export default function FactureEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [facture, setFacture] = useState(null);
  const [parentDevis, setParentDevis] = useState(null);
  const [client, setClient] = useState(null);
  const [form, setForm] = useState({ titre: "", objet: "", lignes: [newLine()] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: f } = await api.get(`/admin/factures/${id}`);
        setFacture(f);
        setForm({
          titre: f.titre || "",
          objet: f.objet || "",
          lignes: f.lignes?.length ? withUids(f.lignes) : [newLine()],
        });
        const { data: c } = await api.get(`/admin/clients/${f.client_id}`);
        setClient(c);
        if (f.devis_id) {
          try {
            const { data: d } = await api.get(`/admin/devis/${f.devis_id}`);
            setParentDevis(d);
          } catch {
            /* parent devis disappeared — keep facture standalone */
          }
        }
      } catch (err) {
        toast.error("Facture introuvable");
        navigate("/admin/factures");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const totals = (() => {
    const ht = form.lignes.reduce((s, l) => s + Number(l.quantite || 0) * Number(l.prix_unitaire || 0), 0);
    const tva = form.lignes.reduce(
      (s, l) => s + (Number(l.quantite || 0) * Number(l.prix_unitaire || 0) * Number(l.tva || 0)) / 100,
      0,
    );
    return { ht, tva, ttc: ht + tva };
  })();

  const setLine = (i, k, v) => {
    setForm((s) => {
      const lignes = [...s.lignes];
      lignes[i] = { ...lignes[i], [k]: v };
      return { ...s, lignes };
    });
  };
  const addLine = () => setForm((s) => ({ ...s, lignes: [...s.lignes, newLine()] }));
  const removeLine = (i) =>
    setForm((s) => ({
      ...s,
      lignes: s.lignes.length > 1 ? s.lignes.filter((_, idx) => idx !== i) : s.lignes,
    }));

  const isLocked = facture?.status === "payee";

  const submit = async (e) => {
    e.preventDefault();
    if (isLocked) {
      toast.error("Cette facture est marquée payée — elle ne peut plus être modifiée.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        titre: form.titre,
        objet: form.objet,
        lignes: form.lignes.map(({ _uid, ...l }) => ({
          ...l,
          quantite: Number(l.quantite),
          prix_unitaire: Number(l.prix_unitaire),
          tva: Number(l.tva),
        })),
      };
      await api.patch(`/admin/factures/${id}`, payload);
      toast.success("Facture mise à jour");
      navigate("/admin/factures");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !facture) return <p className="text-serial">Chargement</p>;

  return (
    <form onSubmit={submit} data-testid="facture-editor">
      <Link to="/admin/factures" className="text-eyebrow inline-flex items-center gap-2 link-underline-static mb-6">
        <ArrowLeft size={14} /> Retour aux factures
      </Link>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-eyebrow mb-3">Facture {facture.numero}</p>
          <h1 className="h-display text-4xl md:text-5xl">
            {facture.type === "acompte" ? "Facture d'acompte" : "Facture de solde"}{" "}
            <span className="serif-italic text-primary">{facture.pourcentage}%</span>
          </h1>
          <p className="text-[13px] text-muted-foreground mt-2">
            Liée au devis {parentDevis?.numero} · {client?.nom}
          </p>
        </div>
      </div>

      {isLocked && (
        <div className="border border-primary/40 bg-primary/5 p-4 mb-8 flex gap-3" data-testid="facture-locked">
          <AlertCircle size={18} className="text-primary mt-0.5 shrink-0" />
          <div className="text-[13.5px] leading-relaxed">
            <p className="font-medium mb-1">Facture payée — verrouillée</p>
            <p className="text-muted-foreground">
              Pour modifier les montants, repasse d'abord le statut sur « Émise » dans la liste des factures.
            </p>
          </div>
        </div>
      )}

      <div className="border border-border bg-muted/30 p-4 mb-8 text-[13px] text-muted-foreground leading-relaxed">
        Les lignes ci-dessous ont été copiées depuis le devis, mises à l'échelle{" "}
        <strong>{facture.pourcentage}%</strong>. Tu peux les modifier librement — ajouter une ligne d'extra,
        retirer un poste, ajuster un prix. Le devis original ne sera pas affecté.
      </div>

      <div className="grid md:grid-cols-12 gap-6 mb-8">
        <div className="md:col-span-7 space-y-5">
          <div>
            <label className="text-eyebrow block mb-2">Titre</label>
            <input
              value={form.titre}
              onChange={(e) => setForm({ ...form, titre: e.target.value })}
              disabled={isLocked}
              className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[15px] outline-none disabled:opacity-60"
              data-testid="facture-titre"
            />
          </div>
          <div>
            <label className="text-eyebrow block mb-2">Objet</label>
            <textarea
              value={form.objet || ""}
              onChange={(e) => setForm({ ...form, objet: e.target.value })}
              disabled={isLocked}
              className="w-full bg-transparent border border-border focus:border-primary p-3 text-[14px] min-h-[70px] disabled:opacity-60"
              data-testid="facture-objet"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-eyebrow">Lignes de la facture</p>
          {!isLocked && (
            <button
              type="button"
              onClick={addLine}
              className="text-eyebrow text-primary link-underline-static inline-flex items-center gap-1"
              data-testid="facture-add-line"
            >
              <Plus size={12} /> Ajouter une ligne
            </button>
          )}
        </div>
        <div className="space-y-4">
          {form.lignes.map((l, i) => (
            <div key={l._uid || `line-${i}`} className="border border-border p-4 grid md:grid-cols-12 gap-3" data-testid={`facture-line-${i}`}>
              <div className="md:col-span-5">
                <label className="text-serial block mb-1">Désignation</label>
                <input
                  value={l.designation}
                  onChange={(e) => setLine(i, "designation", e.target.value)}
                  disabled={isLocked}
                  className="w-full bg-transparent border-b border-border focus:border-primary py-1.5 text-[14px] outline-none disabled:opacity-60"
                />
                <textarea
                  placeholder="Description (optionnelle)"
                  value={l.description || ""}
                  onChange={(e) => setLine(i, "description", e.target.value)}
                  disabled={isLocked}
                  className="w-full bg-transparent border-b border-border focus:border-primary py-1.5 text-[13px] outline-none mt-2 min-h-[40px] disabled:opacity-60"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-serial block mb-1">Qté</label>
                <input
                  type="number"
                  step="0.01"
                  value={l.quantite}
                  onChange={(e) => setLine(i, "quantite", e.target.value)}
                  disabled={isLocked}
                  className="w-full bg-transparent border-b border-border py-1.5 text-[14px] outline-none disabled:opacity-60"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-serial block mb-1">Unité</label>
                <input
                  value={l.unite}
                  onChange={(e) => setLine(i, "unite", e.target.value)}
                  disabled={isLocked}
                  className="w-full bg-transparent border-b border-border py-1.5 text-[14px] outline-none disabled:opacity-60"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-serial block mb-1">PU HT (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={l.prix_unitaire}
                  onChange={(e) => setLine(i, "prix_unitaire", e.target.value)}
                  disabled={isLocked}
                  className="w-full bg-transparent border-b border-border py-1.5 text-[14px] outline-none disabled:opacity-60"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-serial block mb-1">Montant HT</label>
                <div
                  className="w-full border-b border-border py-1.5 text-[14px] text-right tabular-nums text-primary font-medium"
                  data-testid={`facture-line-montant-ht-${i}`}
                >
                  {formatEuro(Number(l.quantite || 0) * Number(l.prix_unitaire || 0))}
                </div>
              </div>
              <div className="md:col-span-1 flex items-end justify-end">
                {!isLocked && form.lignes.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLine(i)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`facture-line-remove-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 grid md:grid-cols-12 gap-6">
        <div className="md:col-span-7" />
        <div className="md:col-span-4 md:col-start-9">
          <div className="border-t border-border pt-4 space-y-2 text-right">
            <p className="text-serial">Net à payer</p>
            <p className="text-3xl font-serif text-primary">{formatEuro(totals.ht)}</p>
            <p className="text-[10px] text-muted-foreground mt-2 italic">
              TVA non applicable, art. 293 B du CGI
            </p>
          </div>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-4 justify-end">
        <Link to="/admin/factures" className="btn-outline" data-testid="facture-cancel">
          Annuler
        </Link>
        <button type="submit" className="btn-terra" disabled={saving || isLocked} data-testid="facture-save">
          {saving ? "Enregistrement…" : "Mettre à jour"}
        </button>
      </div>
    </form>
  );
}
